// ─── Suivi produit (Google Analytics 4) ─────────────────────────────────────
//
// Helpers typés au-dessus de `trackEvent` pour instrumenter le comportement des
// utilisateurs. Un seul endroit pour nommer les événements et leurs paramètres,
// afin que les rapports GA4 restent cohérents. Best-effort : trackEvent avale
// toute erreur, ne casse jamais l'application.
//
// Correspondance métriques ⇄ événements (agrégés dans GA4) :
//   • Recherches des utilisateurs .............. search (search_term)
//   • Examen le plus consulté .................. exam_view (exam_name)
//   • Temps passé sur un examen ................ exam_time_spent (duration_seconds)
//   • Catégories les plus cliquées ............. category_select (category_name)
//   • Nb de questions choisies (pratique) ...... practice_start (question_count)
//   • Examens par jour ......................... simulation_start / practice_start
//   • Super-catégorie la plus utilisée ......... super_category_select (section_*)
//   • Abandon après combien de questions ....... quiz_abandon (answered_count)
import { trackEvent, deviceCategory } from './analytics';
import { logActivity } from './activityApi';

export type QuizKind = 'practice' | 'simulation';

/** Recherche effectuée par l'utilisateur (barre de recherche examens). */
export function trackSearch(searchTerm: string, resultsCount: number): void {
  const term = searchTerm.trim();
  if (!term) return;
  trackEvent('search', {
    search_term: term,
    results_count: resultsCount,
    device_category: deviceCategory(),
  });
  logActivity({
    name: 'search',
    searchTerm: term,
    numValue: resultsCount,
    deviceCategory: deviceCategory(),
  });
}

/** Consultation de la fiche d'un examen (→ « examen le plus visionné »). */
export function trackExamView(examId: string, examName: string): void {
  trackEvent('exam_view', {
    exam_id: examId,
    exam_name: examName,
    device_category: deviceCategory(),
  });
  logActivity({ name: 'exam_view', examId, examName, deviceCategory: deviceCategory() });
}

/** Temps passé sur la fiche d'un examen (émis en quittant la page). */
export function trackExamTimeSpent(
  examId: string,
  examName: string,
  seconds: number,
): void {
  if (!(seconds > 0)) return;
  const rounded = Math.round(seconds);
  trackEvent('exam_time_spent', {
    exam_id: examId,
    exam_name: examName,
    duration_seconds: rounded,
  });
  logActivity({ name: 'exam_time_spent', examId, examName, numValue: rounded });
}

/** Clic sur une catégorie d'un examen (→ « catégories les plus cliquées »). */
export function trackCategorySelect(
  examId: string,
  categoryId: string,
  categoryName: string,
): void {
  trackEvent('category_select', {
    exam_id: examId,
    category_id: categoryId,
    category_name: categoryName,
  });
  logActivity({ name: 'category_select', examId, categoryId, categoryName });
}

/** Lancement d'une simulation complète (→ « examens par jour »). */
export function trackSimulationStart(
  examId: string,
  examName: string,
  questionCount?: number,
): void {
  trackEvent('simulation_start', {
    exam_id: examId,
    exam_name: examName,
    question_count: questionCount,
  });
  logActivity({
    name: 'simulation_start',
    examId,
    examName,
    numValue: questionCount ?? null,
  });
}

/** Lancement d'une session de pratique (nb de questions choisies). */
export function trackPracticeStart(
  examId: string,
  examName: string,
  categoryId: string,
  questionCount?: number,
): void {
  trackEvent('practice_start', {
    exam_id: examId,
    exam_name: examName,
    category_id: categoryId,
    question_count: questionCount,
  });
  logActivity({
    name: 'practice_start',
    examId,
    examName,
    categoryId,
    numValue: questionCount ?? null,
  });
}

/**
 * Utilisation d'une super-catégorie (section d'accueil) : soit en filtrant
 * dessus, soit en ouvrant un examen qui lui appartient. Alimente « la
 * super-catégorie la plus utilisée ».
 */
export function trackSuperCategorySelect(
  sectionSlug: string,
  sectionTitle: string,
  source: 'section_open' | 'exam_click' | 'see_all',
): void {
  if (!sectionSlug) return;
  trackEvent('super_category_select', {
    section_slug: sectionSlug,
    section_title: sectionTitle,
    source,
  });
  logActivity({
    name: 'super_category_select',
    categoryId: sectionSlug,
    categoryName: sectionTitle,
    metadata: { source },
  });
}

/**
 * Abandon d'un quiz (pratique ou simulation) AVANT la fin : combien de
 * questions avaient été répondues au moment de quitter.
 */
export function trackQuizAbandon(
  kind: QuizKind,
  examId: string,
  answeredCount: number,
  totalQuestions: number,
): void {
  const ratio =
    totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;
  trackEvent('quiz_abandon', {
    quiz_type: kind,
    exam_id: examId,
    answered_count: answeredCount,
    total_questions: totalQuestions,
    completion_ratio: ratio,
  });
  logActivity({
    name: 'quiz_abandon',
    examId,
    numValue: answeredCount,
    metadata: { quiz_type: kind, total_questions: totalQuestions, completion_ratio: ratio },
  });
}
